import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MMobileService } from '../../../core/services/common';
import { LifeOperation } from '../../models/operation-register';
import { Document, Policy, PolicyReceipt } from '../../models/policy';
import {
  PolicySaving,
  PolicySavingCertificate
} from '../../models/policy-saving';
import { PendingReceipt } from '../../models/receipt';
import { BaseService } from '../core/base.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class SavingPoliciesService {
  constructor(
    private http: HttpClient,
    private mmobile: MMobileService,
    private base: BaseService,
    private or: OperationRegisterService
  ) {}

  getSavingPolicy({
    policyNumber,
    subpolicyNumber,
    type
  }: PolicySaving): Observable<PolicySaving> {
    let url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${policyNumber}?type=${type}`;
    if (subpolicyNumber) {
      url += `&subpolicyNumber=${subpolicyNumber}`;
    }
    return this.http.get<PolicySaving>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getSavingReceipts(policy: PolicySaving): Observable<PolicyReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${
      policy.policyNumber
    }/receipts`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.setLifeOperationParams(policy);
    return this.http.get<PolicyReceipt[]>(url, { ...headers, params });
  }

  getSavingReceipt(
    policy: PolicySaving,
    receiptNumber: string
  ): Observable<PolicyReceipt> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${
      policy.policyNumber
    }/receipts/${receiptNumber}`;
    const params = this.or.setLifeOperationParams(policy);
    return this.http.get<PolicyReceipt>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params
    });
  }

  getSavingPendingReceipts(
    policyNumber: string,
    params: any
  ): Observable<PendingReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${policyNumber}/receiptsPayment/receipts`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PendingReceipt[]>(url, { ...headers, params });
  }

  getSavingPolicyFiscalCertificates(
    policyNumber: string,
    exercise: string,
    params: HttpParams
  ): Observable<PolicySavingCertificate[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${policyNumber}/certificates?exercise=${exercise}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PolicySavingCertificate[]>(url, { ...headers, params });
  }

  getSavingPolicyBondDocument(policy: PolicySaving, params?: HttpParams) {
    const { policyNumber, bondId, bondPage } = policy;
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${policyNumber}/documents/bonds/${bondId}?bondPage=${bondPage}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, {
      ...headers,
      params
    });
  }

  getSavingBrochurePdf(policy: PolicySaving) {
    const url = this.mmobile.getServiceUrl(
      'URL_FOLLETO_PARTICIPACION_BENIFICIOS'
    );
    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError(error => {
        this.or.registerLifeOperation(
          LifeOperation.PARTICIPACION_BENEFICIOS_KO,
          policy,
          JSON.stringify(error)
        );
        return throwError(error);
      })
    );
  }

  getSavingRenewalNotice(policy: Policy): Observable<Document> {
    const { policyNumber } = policy || {};
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${policyNumber}/documents/renewalNotice`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(
      url,
      { ...headers, params }
    );
  }

  getSavingGeneralConditions(
    policyNumber: string,
    params: HttpParams,
    productCode?: string
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies/${policyNumber}/documents/generalConditions?product=${productCode}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

}
